import { ProductLevelIds } from './selectLists/productLevels'
import { ProductStatusIds } from './selectLists/productStatii'
import { httpGet, httpPost, httpPatchMerge, httpDelete } from './http'
import { getProductKitItemsForKitProductDeepWithContent } from '~/cynqueModules/dataObjects/Product'
import type { TEntityId, IEntityDataItem } from '~/types/models'
import type {
  TProduct,
  TProductOverview,
  IProductKitItem,
  IKitProduct,
} from '~/types/product/models'
import type { ISupplier } from '~/types/supplier/models'
import { isKitProduct, isSetKitProduct } from '~/types/product/models'

const apiEntityPath = '/api/products'

export async function getProducts({ apiPath = apiEntityPath } = {}) {
  const callingMn = getProducts.name
  return httpGet<TProduct[]>({
    callingMn,
    apiPath,
  })
}

export async function getProduct(id: string) {
  const callingMn = getProduct.name
  const apiPath = `${apiEntityPath}/${id}`
  return httpGet<TProduct>({ callingMn, apiPath })
}

export async function getProductOverview(id: string) {
  const callingMn = getProductOverview.name
  const apiPath = `${apiEntityPath}/${id}/overview`
  return httpGet<TProductOverview>({ callingMn, apiPath })
}

export async function getKitItemsForProduct(id: string) {
  const callingMn = getKitItemsForProduct.name
  const apiPath = `${apiEntityPath}/${id}/overview`
  const overviewData = await httpGet<TProductOverview>({ callingMn, apiPath })
  return overviewData.kitItems
}

export async function getProductsFromKitItemsForProduct(id: string) {
  const kitItems = await getKitItemsForProduct(id)
  return kitItems.map((ki) => ki.product)
}

export async function getProductOptionalItemsDeep(
  id: string,
  kitProduct: IKitProduct
) {
  const fn = getProductOptionalItemsDeep.name
  const { items: productKitItemsForKitProductDeep, hasUnconditionalContent } =
    await getProductKitItemsForKitProductDeepWithContent({
      kitProduct,
    })

  const justOptionalItems = productKitItemsForKitProductDeep.filter(
    (item) => item?.isOptional
  )

  console.log(`${fn} just optional items:`, justOptionalItems)

  const justItemsPlusDividers: any[] = []
  justOptionalItems.forEach((item, index) => {
    const selectItem = {
      id: item.id,
      label: item.product.descr,
      parentProduct: item.parentProduct,
      _isFromSet: (item as any)._isFromSet,
      _doAllowNoSelectionFromSet: (item as any)._doAllowNoSelectionFromSet,
      _doAllowMultipleSelectionsFromSet: (item as any)
        ._doAllowMultipleSelectionsFromSet,
      _selectUid: `${item.id}__${item.parentProduct?.id || 'root'}`,
      _parentOptionUid: (item as any)._parentOptionUid,
      // For the "Select at least one option" checks
      // (cynqueModules/kitOptionContent.ts): a select item carries no
      // product to read them from.
      _hasContent: isKitProduct(item.product)
        ? !!(item as any)._hasContent
        : true,
      _isPlainKitOption:
        isKitProduct(item.product) && !isSetKitProduct(item.product),
      _kitHasUnconditionalContent: hasUnconditionalContent,
    }
    justItemsPlusDividers.push(selectItem)
    if (index < justOptionalItems.length - 1) {
      if (
        justOptionalItems[index + 1]?.parentProduct?.id !==
        item.parentProduct?.id
      ) {
        for (let i = 0; i < 2; i++) {
          // V3 shape: Vuetify 2 read a bare `divider: true` on a select item;
          // Vuetify 3 keys off `item.type` (VList.js:25 itemTypes) and renders a
          // real VDivider for it (VSelect.js:514). Under V2's shape the entry had
          // no title and stayed selectable, so each one showed as a blank,
          // caption-less checkbox in the menu. Per-item props go under `props`.
          justItemsPlusDividers.push({
            type: 'divider',
            props: { inset: true },
          })
        }
      }
    }
  })
  console.log(
    `${fn} just optional items with labels and dividers:`,
    justItemsPlusDividers
  )
  return justItemsPlusDividers
}

export async function getSuppliersForProduct(
  id: string,
  wellFormedSupplierProductsOnly: boolean = false
) {
  const callingMn = getSuppliersForProduct.name
  const apiPath =
    `${apiEntityPath}/${id}/suppliers` +
    (wellFormedSupplierProductsOnly
      ? '?wellformedsupplierproductsonly=true'
      : '')
  return httpGet<ISupplier[]>({ callingMn, apiPath })
}

export async function createProduct(entity: TProduct) {
  const callingMn = createProduct.name
  const apiPath = apiEntityPath
  return httpPost<TProduct>({ callingMn, apiPath, entity })
}

export async function updateProduct(
  entityData: Partial<TProduct>,
  id: TEntityId
) {
  const callingMn = updateProduct.name
  const apiPath = `${apiEntityPath}/${id}`
  type TPartialIProduct = Partial<TProduct>
  return httpPatchMerge<TPartialIProduct>({
    callingMn,
    apiPath,
    entityData,
  })
}

export async function deleteProduct(entityData: TProduct) {
  const callingMn = deleteProduct.name
  const apiPath = apiEntityPath
  return httpDelete({
    callingMn,
    apiPath,
    entityData,
  })
}

// Non API-BASED Product functions

export const isValidActiveKit = (item: IEntityDataItem) =>
  isKitProduct(item) &&
  item.status.id === ProductStatusIds.ACTIVE &&
  !item.inKitOnly
// &&
// (item.numKitItems ?? 0) > 0
